// ===============================================================
// File: src/contexts/StorageContext.jsx
// Description: WORKSPACE-BASED Local & Google Drive storage provider for Mudo
// CRITICAL: ALL user data is STRICTLY tied to a selected workspace folder
// - Manual Send/Receive with conflict detection and Force Send
// - Per-file JSON API (loadJSON/saveJSON) used by features
// - readDomain/writeDomain and Master Save/Receive (Files)
// - Boot auto-hydrate of domain files if workspace exists and local is empty
// - Mirrors every save to localStorage as mudo:<path> ONLY when workspace active
// - Auto-restore previously selected folder (IDB) on boot
// - Receipts file is transparently virtualised (sharded) for reliable syncing
// - WORKSPACE ENFORCEMENT: No data loads/saves without active workspace
//
// FIXES INCLUDED (drop-in):
// - Dedupes mudo:workspace-connected event (prevents repeated cascaded loads)
// - Auto-hydrate runs ONCE per workspace (prevents repeated Drive fetch loops)
// - markDirty/markSynced stabilized (prevents callback identity churn)
// ===============================================================

import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";

import {
  ensureGisReady,
  requestTokenFromUserGesture,
  ensureTokenSilently,
  ensureMudoRoot,
  ensureMudoBackups,
  upsertJsonInFolder,
  readJsonFromFolder,
  upsertBlobInFolder,
  readBlobFromFolder,
  deleteFileInFolder,
  healthCheck as driveHealth,
  getDriveUserInfo,
  hasDriveToken,
  setCachedDriveRootId,
} from "@/lib/drive";

import {
  pickWritableDirectory,
  writeJson as writeLocalJson,
  readJson as readLocalJson,
  verifyWrite as verifyLocalWrite,
  writeBlob as writeLocalBlob,
  readBlob as readLocalBlob,
  deleteFile as deleteLocalFile,
} from "@/lib/localFolder";

import {
  restorePersistedDirHandle,
  persistDirHandle,
  clearPersistedDirHandle,
} from "@/storage/persistedHandle";

import { useAuth } from "@/contexts/AuthContext";
import {
  saveFolderHandle,
  getFolderHandle,
  deleteFolderHandle,
} from "@/lib/folderHandleStore";

const StorageContext = createContext(null);
export const useStorage = () => useContext(StorageContext);

// NEW: Debug flag for single-flight/hash-bail logging
const DEBUG_STORAGE = false;

const DEFAULT_SAVE_DEBOUNCE_MS = 750;

const PRIMITIVE_LS_KEYS = [
  "mudo:lastSyncAt",
  "mudo:userRole",
  "mudo:themeMode",
  "mudo:themeAccent",
];

const isDev = import.meta?.env?.MODE === "development";
const debugStorageLog = (...args) => {
  if (isDev) console.log("[STORAGE]", ...args);
};

function safeByteLength(str) {
  try {
    // eslint-disable-next-line no-undef
    return new TextEncoder().encode(String(str || "")).length;
  } catch {
    return String(str || "").length;
  }
}

function ensureDevStorageStats() {
  if (!isDev) return null;
  if (typeof window === "undefined") return null;
  const w = window;
  const created = !w.__mudoStorageStats;
  const stats = w.__mudoStorageStats || {};

  // Core save counters
  stats.requestedSaves = Number(stats.requestedSaves || 0);
  stats.coalescedSaves = Number(stats.coalescedSaves || 0);
  stats.flushedSaves = Number(stats.flushedSaves || 0);
  stats.skippedUnchanged = Number(stats.skippedUnchanged || 0);
  stats.immediateSaves = Number(stats.immediateSaves || 0);

  // Per-key counters
  stats.perKey = stats.perKey && typeof stats.perKey === "object" ? stats.perKey : {};

  // Drive counters (populated by src/lib/drive.js too)
  stats.driveRequests =
    stats.driveRequests && typeof stats.driveRequests === "object"
      ? stats.driveRequests
      : { total: 0, list: 0, get: 0, create: 0, update: 0 };
  stats.driveRequests.total = Number(stats.driveRequests.total || 0);
  stats.driveRequests.list = Number(stats.driveRequests.list || 0);
  stats.driveRequests.get = Number(stats.driveRequests.get || 0);
  stats.driveRequests.create = Number(stats.driveRequests.create || 0);
  stats.driveRequests.update = Number(stats.driveRequests.update || 0);

  stats.getFileIdByName =
    stats.getFileIdByName && typeof stats.getFileIdByName === "object"
      ? stats.getFileIdByName
      : { cacheHit: 0, fetch: 0 };
  stats.getFileIdByName.cacheHit = Number(stats.getFileIdByName.cacheHit || 0);
  stats.getFileIdByName.fetch = Number(stats.getFileIdByName.fetch || 0);

  if (typeof stats.getSnapshot !== "function") {
    stats.getSnapshot = () => {
      const snap = {
        requestedSaves: stats.requestedSaves,
        coalescedSaves: stats.coalescedSaves,
        flushedSaves: stats.flushedSaves,
        skippedUnchanged: stats.skippedUnchanged,
        immediateSaves: stats.immediateSaves,
        perKey: stats.perKey,
        driveRequests: stats.driveRequests,
        getFileIdByName: stats.getFileIdByName,
      };
      try {
        return JSON.parse(JSON.stringify(snap));
      } catch {
        return snap;
      }
    };
  }

  if (typeof stats.reset !== "function") {
    stats.reset = () => {
      stats.requestedSaves = 0;
      stats.coalescedSaves = 0;
      stats.flushedSaves = 0;
      stats.skippedUnchanged = 0;
      stats.immediateSaves = 0;
      stats.perKey = {};
      stats.driveRequests = { total: 0, list: 0, get: 0, create: 0, update: 0 };
      stats.getFileIdByName = { cacheHit: 0, fetch: 0 };
    };
  }

  if (typeof stats.topKeys !== "function") {
    stats.topKeys = (n = 10) => {
      const limit = Math.max(1, Number(n) || 10);
      const entries = Object.entries(stats.perKey || {}).map(([pathKey, v]) => ({
        pathKey,
        ...(v || {}),
      }));
      entries.sort((a, b) => (b.requested || 0) - (a.requested || 0));
      return entries.slice(0, limit);
    };
  }

  stats.__enabled = true;
  w.__mudoStorageStats = stats;

  if (created) {
    // eslint-disable-next-line no-console
    console.log("[Storage] stats enabled");
  }

  return stats;
}

const LS_KEY = "mudo.storage";
const LS_WORKSPACE_KEY = "mudo.activeWorkspace";
const LS_MIGRATED_KEY = "mudo.legacyMigrated";
const MODE_LOCAL = "localSynced";
const MODE_DRIVE = "cloudDrive";

const META_KEY = {
  [MODE_LOCAL]: "mudo.vmeta.localSynced",
  [MODE_DRIVE]: "mudo.vmeta.cloudDrive",
};
const DEVICE_KEY = "mudo.deviceId";

const RESERVED_STORAGE_KEYS = new Set([
  "sb-mudo-global",
  "sb-mudo-global-auth",
  "googleDriveToken",
  "gapi:drive-token",
  "gapi:auth",
]);
const shouldSkipKey = (key) => RESERVED_STORAGE_KEYS.has(key) || key.startsWith("sb-");

const INTERNAL_LS_KEYS = new Set([
  LS_KEY,
  LS_WORKSPACE_KEY,
  LS_MIGRATED_KEY,
  META_KEY[MODE_LOCAL],
  META_KEY[MODE_DRIVE],
  DEVICE_KEY,
  "mudoAuth",
  "mudoLoggedIn",
]);

const RECEIPTS_PATH = "receipts/mudoReceipts.json";
const RECEIPTS_DIR = "receipts/items";
const RECEIPTS_IDX = `${RECEIPTS_DIR}/index.json`;

const DRIVE_CONFIG_PATH = "system/drive-config.json";
export const DRIVE_CONFIG_LS_KEY = "mudo:drive-config:v1";

// Stable stringify with sorted keys to avoid churn in Drive dedupe comparisons
function stableStringify(val) {
  if (val === null || typeof val !== "object") return JSON.stringify(val);
  if (Array.isArray(val)) return `[${val.map((v) => stableStringify(v)).join(",")}]`;
  const keys = Object.keys(val).sort();
  const entries = keys.map((k) => `${JSON.stringify(k)}:${stableStringify(val[k])}`);
  return `{${entries.join(",")}}`;
}

// ✅ FIX #1: coerceJSON – safely parse JSON without throwing
// Handles raw strings ("studio", "dark", "burntBanksia") that are NOT valid JSON
// Never crashes reload loop
function coerceJSON(value) {
  if (value == null) return value;

  // Already a JS object/array/number/bool
  if (typeof value !== "string") return value;

  const s = value.trim();

  // Plain strings like "studio", "dark", "burntBanksia" are NOT JSON
  // Only parse if it looks like JSON (starts with {, [, or is a literal true/false/number)
  if (!s.startsWith("{") && !s.startsWith("[") && s !== "true" && s !== "false" && !/^-?\d+(\.\d+)?$/.test(s)) {
    // It's a plain string value, not JSON
    return value;
  }

  // Looks like JSON, try to parse it
  try {
    return JSON.parse(s);
  } catch {
    // If parse fails, keep it as a string rather than crashing
    return value;
  }
}

// ================================================================================
// CACHE PURGE HELPERS – Global functions for removing items from cached JSON stores
// ================================================================================

/**
 * Remove items from a JSON file in localStorage (and later, Drive)
 * 
 * Usage:
 *   await removeFromJSONList("workshops/workshops.json", (w) => w.id === workshopIdToDelete)
 *   
 * @param {string} filePath - Path like "workshops/workshops.json"
 * @param {Function} predicate - Function returning true for items to remove
 * @returns {Promise<{removed: number, remaining: number}>}
 */
export async function removeFromJSONList(filePath, predicate) {
  if (typeof filePath !== "string" || typeof predicate !== "function") {
    console.warn("[removeFromJSONList] Invalid arguments", { filePath, predicateType: typeof predicate });
    return { removed: 0, remaining: 0 };
  }

  try {
    // Get from localStorage first (highest priority cache)
    const lsKey = `mudo:${filePath}`;
    const lsData = localStorage.getItem(lsKey);
    
    if (lsData) {
      try {
        const list = coerceJSON(lsData);
        if (!Array.isArray(list)) {
          console.warn("[removeFromJSONList] Expected array in localStorage", { lsKey, type: typeof list });
          return { removed: 0, remaining: 0 };
        }

        const beforeCount = list.length;
        const filtered = list.filter((item) => !predicate(item));
        const removed = beforeCount - filtered.length;

        if (removed > 0) {
          localStorage.setItem(lsKey, JSON.stringify(filtered));
          console.log("[removeFromJSONList] Removed from localStorage", {
            filePath,
            lsKey,
            removed,
            remaining: filtered.length,
          });
        }

        return { removed, remaining: filtered.length };
      } catch (lsErr) {
        console.error("[removeFromJSONList] Failed to process localStorage", lsErr?.message);
        return { removed: 0, remaining: 0 };
      }
    }

    console.log("[removeFromJSONList] No cached entry found in localStorage", { filePath });
    return { removed: 0, remaining: 0 };
  } catch (err) {
    console.error("[removeFromJSONList] Error", err?.message);
    return { removed: 0, remaining: 0 };
  }
}

/**
 * Remove a single item from a domain (e.g., "workshops:studioId123")
 * 
 * Usage:
 *   await removeFromLocalCache("workshops:studioId123", (w) => w.id === workshopIdToDelete)
 *   
 * @param {string} cacheKey - Cache key like "workshops:studioId123"
 * @param {Function} predicate - Function returning true for items to remove
 * @returns {Promise<{removed: number, remaining: number}>}
 */
export async function removeFromLocalCache(cacheKey, predicate) {
  if (typeof cacheKey !== "string" || typeof predicate !== "function") {
    console.warn("[removeFromLocalCache] Invalid arguments", { cacheKey, predicateType: typeof predicate });
    return { removed: 0, remaining: 0 };
  }

  try {
    const lsData = localStorage.getItem(cacheKey);
    
    if (lsData) {
      try {
        const list = coerceJSON(lsData);
        if (!Array.isArray(list)) {
          console.warn("[removeFromLocalCache] Expected array", { cacheKey, type: typeof list });
          return { removed: 0, remaining: 0 };
        }

        const beforeCount = list.length;
        const filtered = list.filter((item) => !predicate(item));
        const removed = beforeCount - filtered.length;

        if (removed > 0) {
          localStorage.setItem(cacheKey, JSON.stringify(filtered));
          console.log("[removeFromLocalCache] Removed from localStorage", {
            cacheKey,
            removed,
            remaining: filtered.length,
          });
        }

        return { removed, remaining: filtered.length };
      } catch (lsErr) {
        console.error("[removeFromLocalCache] Failed to process localStorage", lsErr?.message);
        return { removed: 0, remaining: 0 };
      }
    }

    return { removed: 0, remaining: 0 };
  } catch (err) {
    console.error("[removeFromLocalCache] Error", err?.message);
    return { removed: 0, remaining: 0 };
  }
}

/**
 * Broadcast a cache invalidation event (e.g., workshops:changed)
 * 
 * Listeners can update UI immediately without waiting for API roundtrip
 * 
 * @param {string} eventName - Event name like "workshops:changed"
 * @param {Object} detail - Event detail data { studioId, workshopId, etc }
 */
export function broadcastCacheEvent(eventName, detail = {}) {
  if (typeof window === "undefined") return;

  try {
    const event = new CustomEvent(eventName, { 
      detail,
      bubbles: true,
      cancelable: false 
    });
    window.dispatchEvent(event);
    console.log("[broadcastCacheEvent]", eventName, detail);
  } catch (err) {
    console.error("[broadcastCacheEvent] Failed to dispatch event", eventName, err?.message);
  }
}

const driveDebug = (...args) => {
  if (typeof window !== "undefined" && window.__MUDO_DRIVE_DEBUG) {
    console.debug("[StorageContext][Drive]", ...args);
  }
};

const readLS = () => {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || "{}");
  } catch {
    return {};
  }
};
const writeLS = (v) => localStorage.setItem(LS_KEY, JSON.stringify(v));

const isFsDirHandle = (h) =>
  h && typeof h === "object" && typeof h.getDirectoryHandle === "function";

const driveNameFromPath = (p) => String(p).replace(/[\\/]/g, "__");

function splitPath(path) {
  const segs = String(path).split(/[\\/]/g).filter(Boolean);
  const file = segs.pop() || "data.json";
  return { subdirs: segs, file };
}

function getOrCreateDeviceId() {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = `dev_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

function nowIso() {
  return new Date().toISOString();
}
function parseTime(s) {
  const n = Date.parse(s || "");
  return Number.isFinite(n) ? n : 0;
}

function buildVault(snapshot) {
  const deviceId = getOrCreateDeviceId();
  const updatedAt = nowIso();
  const version = String(Date.now());
  return {
    meta: { version, updatedAt, deviceId },
    data: snapshot.data,
  };
}

const DOMAIN_FILES = {
  studio_legacy: "studio.json",
  materials_legacy: "materials.json",
  packaging_legacy: "packaging.json",
  staff_legacy: "staff.json",
  utilities_legacy: "utilities.json",
  marketing_legacy: "marketing.json",
  inspiration_legacy: "inspiration.json",
  tasks_legacy: "tasks.json",
  planner_legacy: "planner.json",
  products_legacy: "products.json",

  auth_profile: "auth/profile.json",
  settings_app: "settings/mudoSettings.json",
  notifications_prefs: "notifications/prefs.json",
  studio_profile: "studio/studioProfile.json",

  staff_file: "mudoStaff.json",
  utilities_file: "mudoUtilities.json",

  materials_domain: "materials.json",

  packaging_items: "packaging/items.json",
  packaging_rules: "packaging/rules.json",

  marketing_file: "marketing/mudoMarketing.json",

  inspiration_items: "inspiration/items.json",

  receipts_file: "receipts/mudoReceipts.json",

  tasks_daily: "tasks/daily.json",

  planner_events: "planner/events.json",
  planner_tasks_sidecar: "planner/plannerTasks.json",

  purchase_orders: "purchase-orders/orders.json",

  workshops_list: "workshops/workshops.json",
  workshops_bookings: "workshops/bookings.json",

  participants_pieces: "participants/pieces.json",

  kiln_primary: "kiln/logs.json",
  kiln_alt: "kiln/kilnLogs.json",

  shopping_list: "shopping/shoppingList.json",

  timelog_active: "timelog/active.json",
  timelog_logs: "timelog/logs.json",
};

// Get list of domain mirror keys only (avoid nuking unrelated prefs like userRole/theme)
function getDomainMirrorKeys() {
  const keys = new Set();
  for (const file of Object.values(DOMAIN_FILES)) {
    keys.add(`mudo:${file}`);
  }
  // Also include legacy keys
  const legacyKeys = ["mudoReceipts", "mudoStaff", "mudoMaterials", "mudoUtilities"];
  for (const lk of legacyKeys) {
    if (localStorage.getItem(lk)) keys.add(lk);
  }
  return Array.from(keys);
}

// Clear all domain data from localStorage (called on workspace disconnect)
function clearAllDomainMirrors() {
  const keys = getDomainMirrorKeys();
  for (const k of keys) {
    try {
      localStorage.removeItem(k);
    } catch {}
  }
}

// Save workspace state
function saveWorkspaceState(workspace) {
  try {
    localStorage.setItem(LS_WORKSPACE_KEY, JSON.stringify(workspace || null));
  } catch {}
}

// Load workspace state
function loadWorkspaceState() {
  try {
    const raw = localStorage.getItem(LS_WORKSPACE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function snapshotLocalStorage() {
  const data = {};
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k || INTERNAL_LS_KEYS.has(k) || shouldSkipKey(k)) {
      if (shouldSkipKey(k)) console.log("[Storage] snapshot: skip reserved key", k);
      continue;
    }
    try {
      data[k] = JSON.parse(localStorage.getItem(k));
    } catch {
      data[k] = localStorage.getItem(k);
    }
  }
  return { version: 1, exportedAt: new Date().toISOString(), data };
}

function readMetaFromLS(mode) {
  try {
    return JSON.parse(localStorage.getItem(META_KEY[mode]) || "null");
  } catch {
    return null;
  }
}
function writeMetaToLS(mode, meta) {
  try {
    localStorage.setItem(META_KEY[mode], JSON.stringify(meta || null));
  } catch {}
}

async function loadReceiptsVirtual(state, hasFolder, isDriveLinked, ensureDriveAuthFn, lastDrivePayloadRef) {
  const readSide = async (relPath) => {
    if (hasFolder) {
      const { readJson } = await import("@/lib/localFolder");
      const { subdirs, file } = splitPath(relPath);
      try {
        return await readJson(state.localHandle, subdirs, file);
      } catch {
        return null;
      }
    }
    if (isDriveLinked && state.driveRootId) {
      const { readJsonFromFolder } = await import("@/lib/drive");
      await ensureDriveAuthFn();
      if (!hasDriveToken()) return null;
      try {
        const data = await readJsonFromFolder(state.driveRootId, driveNameFromPath(relPath));
        try {
          const driveKey = `${state.driveRootId}:${driveNameFromPath(relPath)}`;
          lastDrivePayloadRef?.current?.set(driveKey, stableStringify(data));
        } catch {}
        return data;
      } catch {
        return null;
      }
    }
    return null;
  };

  const index = await readSide(RECEIPTS_IDX);
  if (Array.isArray(index) && index.length) {
    const results = [];
    for (const id of index) {
      const r = await readSide(`${RECEIPTS_DIR}/${id}.json`);
      if (r) results.push(r);
    }
    if (results.length) return results;
  }

  const legacy = await readSide(RECEIPTS_PATH);
  if (Array.isArray(legacy)) return legacy;

  return null;
}

export function saveDriveConfig(config) {
  try {
    if (config) {
      const raw = JSON.stringify(config);
      console.log("[StorageContext] saveDriveConfig → localStorage.setItem", DRIVE_CONFIG_LS_KEY, raw);
      window.localStorage.setItem(DRIVE_CONFIG_LS_KEY, raw);
    } else {
      console.log("[StorageContext] saveDriveConfig → localStorage.removeItem", DRIVE_CONFIG_LS_KEY);
      window.localStorage.removeItem(DRIVE_CONFIG_LS_KEY);
    }
  } catch (err) {
    console.error("[StorageContext] saveDriveConfig error", err);
  }
}

export function loadDriveConfigFromLS() {
  try {
    const raw = window.localStorage.getItem(DRIVE_CONFIG_LS_KEY);
    console.log("[StorageContext] loadDriveConfigFromLS raw", DRIVE_CONFIG_LS_KEY, raw);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed ?? null;
  } catch (err) {
    console.error("[StorageContext] loadDriveConfigFromLS error", err);
    return null;
  }
}

async function saveReceiptsVirtual(state, hasFolder, isDriveLinked, ensureDriveAuthFn, list, opts = {}) {
  const { hasHydratedRef, lastDrivePayloadRef } = opts || {};

  const writeSide = async (relPath, val) => {
    if (hasFolder) {
      const { writeJson } = await import("@/lib/localFolder");
      const { subdirs, file } = splitPath(relPath);
      await writeJson(state.localHandle, subdirs, file, val);
      return true;
    }
    if (isDriveLinked && state.driveRootId) {
      if (hasHydratedRef && !hasHydratedRef.current) {
        driveDebug("skip drive write (receipts) - not hydrated", relPath);
        return false;
      }
      const driveKey = `${state.driveRootId}:${driveNameFromPath(relPath)}`;
      const payloadStr = stableStringify(val);
      if (lastDrivePayloadRef?.current?.get(driveKey) === payloadStr) {
        driveDebug("skip drive write (receipts) - identical payload", driveKey);
        return true;
      }
      lastDrivePayloadRef?.current?.set(driveKey, payloadStr);
      const { upsertJsonInFolder } = await import("@/lib/drive");
      await ensureDriveAuthFn();
      if (!hasDriveToken()) return false;
      await upsertJsonInFolder(state.driveRootId, driveNameFromPath(relPath), val);
      return true;
    }
    return false;
  };

  const safeList = (Array.isArray(list) ? list : []).map((r) => ({
    ...r,
    id: String(r.id || `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`),
  }));
  const safeIds = safeList.map((r) => r.id);

  for (const r of safeList) {
    await writeSide(`${RECEIPTS_DIR}/${r.id}.json`, r);
  }
  await writeSide(RECEIPTS_IDX, safeIds);
  await writeSide(RECEIPTS_PATH, { _sharded: true, count: safeIds.length });

  return true;
}

export function StorageProvider({ children }) {
  // Per-user folder remember constants
  const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
  const FOLDER_META_KEY = (userId) => `mudo-folder-meta:${userId}`;
  const FOLDER_HANDLE_KEY = (userId) => `mudo-folder-handle:${userId}`;

  const { user, isAuthReady } = useAuth() || {};

  const boot = readLS();
  const [state, setState] = useState(boot);
  const [busy, setBusy] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const [lastConflict, setLastConflict] = useState(null);

  // Restore progress state for UI overlay
  const [restorePhase, setRestorePhase] = useState("idle"); // idle|starting|listing|downloading|hydrating|done|error
  const [restoreProgress, setRestoreProgress] = useState(0); // 0-100

  // Drive hydration + dedupe guards
  const hasHydratedRef = useRef(false);
  const lastDrivePayloadRef = useRef(new Map());
  const suspendDriveWritesRef = useRef(false);

  // ✅ FIX #2: Single-flight reload guard – prevent re-entrant reload calls
  const reloadInFlightRef = useRef(null);

  // Single-flight receive guard – prevent concurrent receive storms
  const receiveInFlightRef = useRef(null);

  // DEV-only audit: track loadJSON/saveJSON call frequency
  const auditCallsRef = useRef(new Map());

  // NEW: Single-flight load guards (prevent concurrent identical loads)
  const inflightLoadsRef = useRef(new Map());
  // NEW: Single-flight save guards (prevent concurrent identical saves)
  const inflightSavesRef = useRef(new Map());
  // NEW: Content hash for idempotent saves (skip saves when content unchanged)
  const lastSavedHashRef = useRef(new Map());
  // NEW: Debounced/coalesced saves per key (reduces Drive/local write storms)
  const debouncedSavesRef = useRef(new Map());

  // Binary file URL cache (object URLs) – keyed by relPath
  const fileUrlCacheRef = useRef(new Map());

  // Revoke all cached URLs on unmount
  useEffect(() => {
    return () => {
      try {
        for (const v of fileUrlCacheRef.current.values()) {
          if (v?.url) URL.revokeObjectURL(v.url);
        }
        fileUrlCacheRef.current.clear();
      } catch {}
    };
  }, []);

  // Drive config hydration
  const [driveConfig, setDriveConfig] = useState(null);
  const [isDriveLinkedFlag, setIsDriveLinkedFlag] = useState(false);
  const [bootstrappingDrive, setBootstrappingDrive] = useState(false);
  const [receiveSource, setReceiveSource] = useState("none"); // none|manual|auto-boot

  // Active workspace state (null = no workspace = empty app)
  const [activeWorkspace, setActiveWorkspace] = useState(() => loadWorkspaceState());

  const mode = state.mode || null;
  const localUsable = isFsDirHandle(state.localHandle);
  const hasFolder = mode === MODE_LOCAL && localUsable;

  const isDriveLinked =
    (mode === MODE_DRIVE && !!state.driveRootId) || !!isDriveLinkedFlag || !!driveConfig?.folderId;
  const isDriveAuthed = isDriveLinked && hasDriveToken();

  // CRITICAL: workspace requires activeWorkspace + a usable backing store (folder OR drive+token)
  const hasWorkspace = activeWorkspace !== null && (hasFolder || (isDriveLinked && isDriveAuthed));

  const baseDirName =
    mode === MODE_LOCAL ? (state.localName || "—") : mode === MODE_DRIVE ? "Google Drive /Mudo" : "—";

  const workspaceName =
    mode === MODE_LOCAL ? (state.localName || "") : mode === MODE_DRIVE ? (state.driveUser || "Google Drive") : "";

  // ---------------------------------------------------------------------------
  // DEDUPE: workspace-connected event (prevents repeated downstream loads)
  // ---------------------------------------------------------------------------
  const lastWorkspaceKeyRef = useRef("");
  const emitWorkspaceConnectedOnce = useCallback((workspace) => {
    if (!workspace) return;
    const key = `${workspace.type}:${workspace.id}`;
    if (!key) return;
    if (lastWorkspaceKeyRef.current === key) return;
    lastWorkspaceKeyRef.current = key;
    try {
      window.dispatchEvent(new CustomEvent("mudo:workspace-connected", { detail: workspace }));
    } catch {}
  }, []);

  // ---------------------------------------------------------------------------
  // Stabilize dirty markers (prevents callback identity churn)
  // ---------------------------------------------------------------------------
  const markDirty = useCallback(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem("mudo:dirty", "1");
    } catch {}
  }, []);

  const markSynced = useCallback(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.removeItem("mudo:dirty");
      window.localStorage.setItem("mudo:lastSyncAt", new Date().toISOString());
    } catch {}
  }, []);

  // One-time: normalize certain primitive localStorage keys that may have been JSON-stringified
  useEffect(() => {
    if (typeof window === "undefined") return;
    for (const key of PRIMITIVE_LS_KEYS) {
      try {
        const raw = window.localStorage.getItem(key);
        if (raw == null) continue;
        // If stored as JSON (e.g. "\"visitor\""), normalize back to raw string.
        const parsed = JSON.parse(raw);
        if (typeof parsed === "string" || typeof parsed === "number" || typeof parsed === "boolean") {
          window.localStorage.setItem(key, String(parsed));
        }
      } catch {
        // Not JSON; keep as-is
      }
    }
  }, []);

  // One-time hydration of Drive config from localStorage
  useEffect(() => {
    const stored = loadDriveConfigFromLS();
    if (stored) {
      console.log("[StorageContext] Restored drive config from localStorage", stored);
      setDriveConfig(stored);
      setIsDriveLinkedFlag(true);
      (async () => {
        setBootstrappingDrive(true);
        const ok = await bootstrapDriveWorkspaceFromConfig(stored);
        if (!ok) setBootstrappingDrive(false);
      })();
    } else {
      console.log("[StorageContext] No drive config found in localStorage");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------------
  // Drive bootstrap from config (idempotent, no UI popups)
  // ---------------------------------------------------------------------------
  const bootstrapDriveWorkspaceFromConfig = useCallback(
    async (config) => {
      try {
        if (!config || !config.folderId) return false;
        if (state.mode === MODE_DRIVE && state.driveRootId === config.folderId) return true;

        try {
          await ensureGisReady();
        } catch {}
        try {
          await ensureTokenSilently();
        } catch (e) {
          console.warn("[StorageContext] Silent Drive auth failed during bootstrap", e);
          return false;
        }

        try {
          setCachedDriveRootId(config.folderId);
        } catch {}

        const next = {
          mode: MODE_DRIVE,
          driveRootId: config.folderId,
          driveUser: config.linkedEmail || state.driveUser || "Google Drive",
        };
        setState((prev) => ({ ...(prev || {}), ...next }));
        writeLS({ mode: next.mode, driveRootId: next.driveRootId, driveUser: next.driveUser });
        writeMetaToLS(MODE_DRIVE, null);
        setLastConflict(null);

        const workspace = { type: "drive", id: next.driveRootId, name: next.driveUser };
        setActiveWorkspace(workspace);
        saveWorkspaceState(workspace);
        emitWorkspaceConnectedOnce(workspace);

        console.log("[StorageContext] Bootstrapped Drive workspace from config", config);
        return true;
      } catch (err) {
        console.error("[StorageContext] bootstrapDriveWorkspaceFromConfig error", err);
        return false;
      }
    },
    [emitWorkspaceConnectedOnce, state.driveRootId, state.driveUser, state.mode]
  );

  // ---------------------------------------------------------------------------
  // Local folder restore (per-user, then legacy)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    (async () => {
      if (!isAuthReady) return;
      if (mode || hasFolder) return;

      // Per-user remembered handle (30-day expiry, silent permission check)
      try {
        const userId = user?.id;
        if (userId && window?.indexedDB) {
          const metaRaw = localStorage.getItem(FOLDER_META_KEY(userId));
          const meta = metaRaw ? JSON.parse(metaRaw) : null;
          const now = Date.now();
          const notExpired = meta && typeof meta.expiresAt === "number" && meta.expiresAt > now;
          if (notExpired) {
            const key = FOLDER_HANDLE_KEY(userId);
            const handle = await getFolderHandle(key);
            const q = await handle?.queryPermission?.({ mode: "readwrite" });
            const granted = q === "granted";
            if (handle && granted) {
              const name = handle.name || "Selected Folder";
              const next = { mode: MODE_LOCAL, localHandle: handle, localName: name };
              setState(next);
              writeLS({ mode: MODE_LOCAL, localName: name });
              writeMetaToLS(MODE_LOCAL, null);
              setLastConflict(null);

              const workspace = { type: "local", id: name, name, handle };
              setActiveWorkspace(workspace);
              saveWorkspaceState(workspace);
              emitWorkspaceConnectedOnce(workspace);
              return;
            }
          }
        }
      } catch (e) {
        console.warn("[StorageContext] per-user folder restore skipped", e);
      }

      // Legacy single-handle restore
      try {
        const persisted = await restorePersistedDirHandle();
        if (persisted) {
          const q = await persisted.queryPermission?.({ mode: "readwrite" });
          if (q === "granted") {
            const name = persisted.name || "Selected Folder";
            const next = { mode: MODE_LOCAL, localHandle: persisted, localName: name };
            setState(next);
            writeLS({ mode: MODE_LOCAL, localName: name });
            writeMetaToLS(MODE_LOCAL, null);
            setLastConflict(null);

            const workspace = { type: "local", id: name, name, handle: persisted };
            setActiveWorkspace(workspace);
            saveWorkspaceState(workspace);
            emitWorkspaceConnectedOnce(workspace);
          }
        }
      } catch {}
    })();
  }, [mode, hasFolder, user?.id, isAuthReady, emitWorkspaceConnectedOnce]);

  // ---------------------------------------------------------------------------
  // Pick local folder
  // ---------------------------------------------------------------------------
  const pickFolder = useCallback(async () => {
    try {
      const handle = await pickWritableDirectory();
      const perm = await handle.requestPermission({ mode: "readwrite" });
      if (perm !== "granted") throw new Error("Permission not granted.");
      const name = handle.name || "Selected Folder";

      const next = { mode: MODE_LOCAL, localHandle: handle, localName: name };
      setState(next);
      writeLS({ mode: MODE_LOCAL, localName: name });
      writeMetaToLS(MODE_LOCAL, null);
      setLastConflict(null);
      await persistDirHandle(handle);

      // Per-user remember (30 days)
      try {
        const uid = user?.id;
        if (uid && window?.indexedDB) {
          const key = FOLDER_HANDLE_KEY(uid);
          await saveFolderHandle(key, handle);
          const linkedAt = Date.now();
          const meta = {
            userId: uid,
            linkedAt,
            expiresAt: linkedAt + THIRTY_DAYS_MS,
            type: "local-folder",
          };
          localStorage.setItem(FOLDER_META_KEY(uid), JSON.stringify(meta));
        }
      } catch (e) {
        console.warn("[StorageContext] per-user folder save failed", e);
      }

      const workspace = { type: "local", id: name, name, handle };
      setActiveWorkspace(workspace);
      saveWorkspaceState(workspace);
      emitWorkspaceConnectedOnce(workspace);

      return true;
    } catch (e) {
      console.warn("[Storage] pickFolder failed:", e);
    }
    return false;
  }, [emitWorkspaceConnectedOnce, user?.id]);

  const clearFolderSelection = useCallback(() => {
    setActiveWorkspace(null);
    saveWorkspaceState(null);

    setState({});
    writeLS({});
    writeMetaToLS(MODE_LOCAL, null);
    writeMetaToLS(MODE_DRIVE, null);
    setLastConflict(null);
    clearPersistedDirHandle();

    clearAllDomainMirrors();

    try {
      window.dispatchEvent(new Event("mudo:workspace-disconnected"));
    } catch {}
  }, []);

  // ---------------------------------------------------------------------------
  // Drive connect / disconnect
  // ---------------------------------------------------------------------------
  const connectDrive = useCallback(async () => {
    if (busy) return false;
    setBusy(true);
    try {
      if (!window.google?.accounts?.oauth2) {
        try {
          await ensureGisReady();
        } catch (e) {
          alert(
            "Google services are still loading. Please try again in a moment.\n\n" +
              (e?.message || String(e))
          );
          return false;
        }
      }

      try {
        await requestTokenFromUserGesture();
      } catch (popupErr) {
        const msg = String(popupErr?.message || popupErr || "").toLowerCase();
        if (msg.includes("popup") || msg.includes("blocked") || msg.includes("window")) {
          alert(
            "The Google sign-in popup was blocked. Please allow popups for this site and click Connect Google Drive again."
          );
        } else {
          alert(
            "Google Drive sign-in failed. Please try again.\n\n" +
              (popupErr?.message || String(popupErr))
          );
        }
        return false;
      }

      const rootId = await ensureMudoRoot();
      await ensureMudoBackups(rootId);
      await driveHealth(rootId);
      const driveUser = await getDriveUserInfo();
      setCachedDriveRootId(rootId);

      const next = {
        mode: MODE_DRIVE,
        driveRootId: rootId,
        driveUser: driveUser?.emailAddress || driveUser?.displayName || "Google Drive",
      };
      setState(next);
      writeLS({ mode: next.mode, driveRootId: next.driveRootId, driveUser: next.driveUser });
      writeMetaToLS(MODE_DRIVE, null);
      setLastConflict(null);

      const nextDriveConfig = {
        driveProvider: "google",
        folderId: rootId,
        folderName: "/Mudo",
        linkedEmail: driveUser?.emailAddress || "",
        lastLinkedAt: nowIso(),
      };
      saveDriveConfig(nextDriveConfig);

      // Mirror into vault JSON for cross-device receive (best effort)
      try {
        // NOTE: saveJSON defined later; we’ll mirror after it exists via load/save APIs.
        // Kept as best effort but non-blocking.
      } catch {}

      setDriveConfig(nextDriveConfig);
      setIsDriveLinkedFlag(true);

      const workspace = { type: "drive", id: rootId, name: next.driveUser };
      setActiveWorkspace(workspace);
      saveWorkspaceState(workspace);
      emitWorkspaceConnectedOnce(workspace);

      console.log("[StorageContext] Drive linked and config saved", nextDriveConfig);
      return true;
    } catch (e) {
      alert(
        "Google Drive link failed.\n\n" +
          (e?.message || String(e)) +
          "\n\nAllow pop-ups and ensure OAuth origins include this URL."
      );
      return false;
    } finally {
      setBusy(false);
    }
  }, [busy, emitWorkspaceConnectedOnce]);

  const disconnectDrive = useCallback(() => {
    setActiveWorkspace(null);
    saveWorkspaceState(null);

    setState({});
    writeLS({});
    writeMetaToLS(MODE_DRIVE, null);
    setLastConflict(null);

    try {
      localStorage.removeItem(DRIVE_CONFIG_LS_KEY);
    } catch {}
    setDriveConfig(null);
    setIsDriveLinkedFlag(false);

    clearAllDomainMirrors();

    try {
      window.dispatchEvent(new Event("mudo:workspace-disconnected"));
    } catch {}
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await ensureGisReady();
      } catch {}
    })();
  }, [isDriveLinked]);

  const ensureDriveAuth = useCallback(async () => {
    if (isDriveLinked && !hasDriveToken()) {
      try {
        await ensureTokenSilently();
      } catch (err) {
        // NEW: Handle auth required/cooldown errors gracefully - don't spam logs
        if (err?.message === "DRIVE_AUTH_REQUIRED" || err?.message === "DRIVE_AUTH_COOLDOWN") {
          // Silent fail - background calls will just return cached/fallback values
          return;
        }
        // Other errors can be logged
        if (import.meta.env.DEV) {
          console.warn("[Storage] ensureDriveAuth failed:", err?.message);
        }
      }
    }
  }, [isDriveLinked]);

  const requestDriveTokenInteractive = useCallback(async () => {
    if (!isDriveLinked) return false;
    try {
      await requestTokenFromUserGesture();
      return true;
    } catch (err) {
      console.error("[Storage] requestDriveTokenInteractive failed", err);
      throw err;
    }
  }, [isDriveLinked]);

  // Backfill activeWorkspace if storage state exists (legacy sessions)
  useEffect(() => {
    if (activeWorkspace) return;

    if (hasFolder && state.localHandle) {
      const name = state.localName || "Selected Folder";
      const workspace = { type: "local", id: name, name, handle: state.localHandle };
      setActiveWorkspace(workspace);
      saveWorkspaceState(workspace);
      emitWorkspaceConnectedOnce(workspace);
      return;
    }

    if (isDriveLinked && isDriveAuthed && state.driveRootId) {
      const workspace = {
        type: "drive",
        id: state.driveRootId,
        name: state.driveUser || "Google Drive",
      };
      setActiveWorkspace(workspace);
      saveWorkspaceState(workspace);
      emitWorkspaceConnectedOnce(workspace);
    }
  }, [
    activeWorkspace,
    hasFolder,
    isDriveLinked,
    isDriveAuthed,
    state.localHandle,
    state.localName,
    state.driveRootId,
    state.driveUser,
    emitWorkspaceConnectedOnce,
  ]);

  // ---------------------------------------------------------------------------
  // Core JSON APIs
  // ---------------------------------------------------------------------------
  const normalizePathKey = (p) => {
    if (!p) return "";
    let s = String(p).trim();
    if (s.startsWith("./")) s = s.slice(2);
    const q = s.indexOf("?");
    if (q >= 0) s = s.slice(0, q);
    const h = s.indexOf("#");
    if (h >= 0) s = s.slice(0, h);
    return s;
  };

  const FILE_READ_ALIASES = {
    "kiln/logs.json": ["kiln/kilnLogs.json"],
    "kiln/kilnLogs.json": ["kiln/logs.json"],
  };

  const loadJSON = useCallback(
    async (path, fallback = null, opts = {}) => {
      const pathKey = normalizePathKey(path);
      if (!pathKey) return fallback;

      // NEW: Single-flight guard - return existing promise if already loading
      if (inflightLoadsRef.current.has(pathKey)) {
        if (DEBUG_STORAGE) console.debug("[Storage] LOAD_INFLIGHT", pathKey);
        try {
          return await inflightLoadsRef.current.get(pathKey);
        } catch {
          return fallback;
        }
      }

      // DEV-only audit logging
      if (import.meta.env.DEV) {
        const reason = opts?.reason || "unknown";
        const key = `${pathKey}:${reason}`;
        const count = (auditCallsRef.current.get(key) || 0) + 1;
        auditCallsRef.current.set(key, count);
        if (count <= 3) {
          console.log(`[Storage] loadJSON ${pathKey} reason=${reason} count=${count}`);
        }
      }

      if (!hasWorkspace) return fallback;

      // NEW: Create promise and track it
      const loadPromise = (async () => {

      try {
        const mirrored = localStorage.getItem(`mudo:${pathKey}`);
        if (mirrored != null) return JSON.parse(mirrored);
        if (String(pathKey) === RECEIPTS_PATH) {
          const legacy = localStorage.getItem("mudoReceipts");
          if (legacy != null) return JSON.parse(legacy);
        }
      } catch {}

      if (String(pathKey) === RECEIPTS_PATH) {
        try {
          const data = await loadReceiptsVirtual(
            state,
            hasFolder,
            isDriveLinked,
            ensureDriveAuth,
            lastDrivePayloadRef
          );
          return data ?? fallback;
        } catch {}
      }

      if (hasFolder) {
        const { subdirs, file } = splitPath(pathKey);
        try {
          const data = await readLocalJson(state.localHandle, subdirs, file);
          return data ?? fallback;
        } catch {
          // fall through
        }

        const aliases = FILE_READ_ALIASES[pathKey];
        if (aliases && aliases.length) {
          for (const alias of aliases) {
            try {
              const { subdirs: aSub, file: aFile } = splitPath(alias);
              const aliasData = await readLocalJson(state.localHandle, aSub, aFile);
              if (aliasData != null) return aliasData;
            } catch {}
          }
        }
        return fallback;
      }

      if (isDriveLinked && state.driveRootId) {
        await ensureDriveAuth();
        if (!hasDriveToken()) return fallback;
        try {
          const data = await readJsonFromFolder(state.driveRootId, driveNameFromPath(pathKey));
          try {
            const driveKey = `${state.driveRootId}:${driveNameFromPath(pathKey)}`;
            lastDrivePayloadRef.current.set(driveKey, stableStringify(data));
          } catch {}
          return data ?? fallback;
        } catch {}

        const aliases = FILE_READ_ALIASES[pathKey];
        if (aliases && aliases.length) {
          for (const alias of aliases) {
            try {
              const data = await readJsonFromFolder(state.driveRootId, driveNameFromPath(alias));
              if (data != null) return data;
            } catch {}
          }
        }
        return fallback;
      }

      return fallback;
      })(); // NEW: end of async IIFE

      // NEW: Store promise and cleanup in finally
      inflightLoadsRef.current.set(pathKey, loadPromise);
      try {
        return await loadPromise;
      } finally {
        inflightLoadsRef.current.delete(pathKey);
      }
    },
    [hasWorkspace, hasFolder, isDriveLinked, state.localHandle, state.driveRootId, ensureDriveAuth, state]
  );

  const loadJSONFresh = useCallback(
    async (path, fallback = null, opts = {}) => {
      const pathKey = normalizePathKey(path);
      if (!pathKey) return fallback;

      // NEW: Single-flight guard - use separate key for fresh loads
      const freshKey = `${pathKey}:fresh`;
      if (inflightLoadsRef.current.has(freshKey)) {
        try {
          return await inflightLoadsRef.current.get(freshKey);
        } catch {
          return fallback;
        }
      }

      // DEV-only audit logging
      if (import.meta.env.DEV) {
        const reason = opts?.reason || "unknown";
        const key = `${pathKey}:fresh:${reason}`;
        const count = (auditCallsRef.current.get(key) || 0) + 1;
        auditCallsRef.current.set(key, count);
        if (count <= 3) {
          console.log(`[Storage] loadJSONFresh ${pathKey} reason=${reason} count=${count}`);
        }
      }

      if (!hasWorkspace) return fallback;

      // NEW: Create promise and track it
      const loadPromise = (async () => {

      if (String(pathKey) === RECEIPTS_PATH) {
        try {
          const data = await loadReceiptsVirtual(
            state,
            hasFolder,
            isDriveLinked,
            ensureDriveAuth,
            lastDrivePayloadRef
          );
          return data ?? fallback;
        } catch {}
      }

      if (hasFolder) {
        const { subdirs, file } = splitPath(pathKey);
        try {
          const data = await readLocalJson(state.localHandle, subdirs, file);
          return data ?? fallback;
        } catch {}

        const aliases = FILE_READ_ALIASES[pathKey];
        if (aliases && aliases.length) {
          for (const alias of aliases) {
            try {
              const { subdirs: aSub, file: aFile } = splitPath(alias);
              const aliasData = await readLocalJson(state.localHandle, aSub, aFile);
              if (aliasData != null) return aliasData;
            } catch {}
          }
        }
        return fallback;
      }

      if (isDriveLinked && state.driveRootId) {
        await ensureDriveAuth();
        if (!hasDriveToken()) return fallback;
        try {
          const data = await readJsonFromFolder(state.driveRootId, driveNameFromPath(pathKey));
          try {
            const driveKey = `${state.driveRootId}:${driveNameFromPath(pathKey)}`;
            lastDrivePayloadRef.current.set(driveKey, stableStringify(data));
          } catch {}
          return data ?? fallback;
        } catch {}

        const aliases = FILE_READ_ALIASES[pathKey];
        if (aliases && aliases.length) {
          for (const alias of aliases) {
            try {
              const data = await readJsonFromFolder(state.driveRootId, driveNameFromPath(alias));
              if (data != null) return data;
            } catch {}
          }
        }
        return fallback;
      }

      return fallback;
      })(); // NEW: end of async IIFE

      // NEW: Store promise and cleanup in finally
      inflightLoadsRef.current.set(freshKey, loadPromise);
      try {
        return await loadPromise;
      } finally {
        inflightLoadsRef.current.delete(freshKey);
      }
    },
    [hasWorkspace, hasFolder, isDriveLinked, state.localHandle, state.driveRootId, ensureDriveAuth, state]
  );

  const saveJSON = useCallback(
    async (path, value, opts = {}) => {
      const pathKey = normalizePathKey(path);
      if (!pathKey) return false;

      const stats = ensureDevStorageStats();
      if (stats) {
        stats.requestedSaves += 1;
        const now = Date.now();
        const pk =
          stats.perKey[pathKey] ||
          (stats.perKey[pathKey] = {
            requested: 0,
            flushed: 0,
            skippedUnchanged: 0,
            lastRequestedAt: 0,
            lastFlushedAt: 0,
            lastSizeBytes: 0,
          });
        pk.requested += 1;
        pk.lastRequestedAt = now;
      }

      if (!hasWorkspace) return false;

      const debounceMs = Number.isFinite(opts?.debounceMs)
        ? Math.max(0, Number(opts.debounceMs))
        : DEFAULT_SAVE_DEBOUNCE_MS;
      const immediate = opts?.immediate === true || debounceMs === 0;

      if (stats && immediate) stats.immediateSaves += 1;

      const shouldWarnNonJsonExisting = pathKey.includes("/") || pathKey.endsWith(".json");

      const doImmediateSave = async (valueToSave, runOpts = opts) => {
        // Content hash idempotency check - skip if unchanged
        const contentHash = stableStringify(valueToSave);
        if (lastSavedHashRef.current.get(pathKey) === contentHash) {
          if (DEBUG_STORAGE) console.debug("[Storage] SAVE_BAIL hash match", pathKey);
          if (stats) {
            stats.skippedUnchanged += 1;
            const pk = stats.perKey[pathKey];
            if (pk) pk.skippedUnchanged += 1;
          }
          if (import.meta.env.DEV && runOpts?.reason) {
            const reason = runOpts?.reason || "unknown";
            const key = `${pathKey}:save:skipped:${reason}`;
            const count = (auditCallsRef.current.get(key) || 0) + 1;
            auditCallsRef.current.set(key, count);
            if (count <= 3) {
              console.log(
                `[Storage] saveJSON ${pathKey} skipped - content unchanged (reason=${reason} count=${count})`
              );
            }
          }
          return true;
        }

        // Serialize saves per key (mutex)
        const inflight = inflightSavesRef.current.get(pathKey);
        if (inflight) {
          try {
            await inflight;
          } catch {
            // ignore
          }
        }
        // Re-check after waiting (another writer may have saved the same content)
        if (lastSavedHashRef.current.get(pathKey) === contentHash) return true;

        if (stats) {
          stats.flushedSaves += 1;
          const pk = stats.perKey[pathKey];
          if (pk) {
            pk.flushed += 1;
            pk.lastFlushedAt = Date.now();
            pk.lastSizeBytes = safeByteLength(contentHash);
          }
        }

        // DEV-only audit logging
        if (import.meta.env.DEV) {
          const reason = runOpts?.reason || "unknown";
          const key = `${pathKey}:save:${reason}`;
          const count = (auditCallsRef.current.get(key) || 0) + 1;
          auditCallsRef.current.set(key, count);
          if (count <= 3) {
            console.log(`[Storage] saveJSON ${pathKey} reason=${reason} count=${count}`);
          }
        }

        const savePromise = (async () => {
          // Mirror to localStorage only when workspace is active
          try {
            const mirrorKey = `mudo:${pathKey}`;
            const nextStr = JSON.stringify(valueToSave);
            const existing = localStorage.getItem(mirrorKey);
            if (existing != null && existing !== nextStr) {
              if (shouldWarnNonJsonExisting) {
                try {
                  JSON.parse(existing);
                } catch {
                  console.warn("[Storage] saveJSON existing value not JSON, overwriting", pathKey);
                }
              }
            }
            if (existing !== nextStr) {
              localStorage.setItem(mirrorKey, nextStr);
              if (String(pathKey) === RECEIPTS_PATH) {
                localStorage.setItem("mudoReceipts", nextStr);
              }
            }
          } catch {}

          if (String(pathKey) === RECEIPTS_PATH && Array.isArray(valueToSave)) {
            return await saveReceiptsVirtual(state, hasFolder, isDriveLinked, ensureDriveAuth, valueToSave, {
              hasHydratedRef,
              lastDrivePayloadRef,
            });
          }

          if (hasFolder) {
            const { subdirs, file } = splitPath(pathKey);
            await writeLocalJson(state.localHandle, subdirs, file, valueToSave);
            markDirty();
            return true;
          }

          if (isDriveLinked && state.driveRootId) {
            if (!hasHydratedRef.current) {
              driveDebug("skip drive write - not hydrated", pathKey);
              return true;
            }
            if (suspendDriveWritesRef.current) {
              driveDebug("skip drive write - receiving/applying remote", pathKey);
              return true;
            }
            const driveKey = `${state.driveRootId}:${driveNameFromPath(pathKey)}`;
            const payloadStr = stableStringify(valueToSave);
            if (lastDrivePayloadRef.current.get(driveKey) === payloadStr) {
              driveDebug("skip drive write - identical payload", driveKey);
              return true;
            }
            await ensureDriveAuth();
            if (!hasDriveToken()) return false;
            lastDrivePayloadRef.current.set(driveKey, payloadStr);
            await upsertJsonInFolder(state.driveRootId, driveNameFromPath(pathKey), valueToSave);
            markDirty();
            return true;
          }

          markDirty();
          return true;
        })();

        inflightSavesRef.current.set(pathKey, savePromise);
        try {
          const result = await savePromise;
          if (result) {
            if (DEBUG_STORAGE) console.debug("[Storage] SAVE_WRITE", pathKey);
            lastSavedHashRef.current.set(pathKey, contentHash);
          }
          return result;
        } finally {
          inflightSavesRef.current.delete(pathKey);
        }
      };

      if (!immediate) {
        const existing = debouncedSavesRef.current.get(pathKey);
        if (existing) {
          existing.value = value;
          existing.opts = opts;
          if (stats) stats.coalescedSaves += 1;
          if (existing.timer) clearTimeout(existing.timer);
          existing.timer = setTimeout(async () => {
            const entry = debouncedSavesRef.current.get(pathKey);
            if (!entry) return;
            debouncedSavesRef.current.delete(pathKey);
            try {
              const ok = await doImmediateSave(entry.value, entry.opts);
              entry.resolve(ok);
            } catch (e) {
              entry.reject(e);
            }
          }, debounceMs);
          return existing.promise;
        }

        let resolve;
        let reject;
        const promise = new Promise((res, rej) => {
          resolve = res;
          reject = rej;
        });
        const entry = { value, opts, promise, resolve, reject, timer: null };
        entry.timer = setTimeout(async () => {
          const current = debouncedSavesRef.current.get(pathKey);
          if (!current) return;
          debouncedSavesRef.current.delete(pathKey);
          try {
            const ok = await doImmediateSave(current.value, current.opts);
            current.resolve(ok);
          } catch (e) {
            current.reject(e);
          }
        }, debounceMs);
        debouncedSavesRef.current.set(pathKey, entry);
        return promise;
      }

      return await doImmediateSave(value, opts);
    },
    [
      hasWorkspace,
      hasFolder,
      isDriveLinked,
      state.localHandle,
      state.driveRootId,
      ensureDriveAuth,
      state,
      markDirty,
    ]
  );

  // ---------------------------------------------------------------------------
  // Binary file APIs (Blobs) – for images/attachments
  // ---------------------------------------------------------------------------
  const saveFile = useCallback(
    async (relPath, blob, opts = {}) => {
      const pathKey = normalizePathKey(relPath);
      if (!pathKey) return false;
      if (!hasWorkspace) return false;

      const mimeType = String(opts?.mimeType || blob?.type || "application/octet-stream");

      if (hasFolder) {
        const { subdirs, file } = splitPath(pathKey);
        await writeLocalBlob(state.localHandle, subdirs, file, blob);
        markDirty();
        return true;
      }

      if (isDriveLinked && state.driveRootId) {
        if (!hasHydratedRef.current) {
          driveDebug("skip drive blob write - not hydrated", pathKey);
          return true;
        }
        if (suspendDriveWritesRef.current) {
          driveDebug("skip drive blob write - receiving/applying remote", pathKey);
          return true;
        }
        await ensureDriveAuth();
        if (!hasDriveToken()) return false;
        await upsertBlobInFolder(state.driveRootId, driveNameFromPath(pathKey), blob, mimeType);
        markDirty();
        return true;
      }

      markDirty();
      return true;
    },
    [hasWorkspace, hasFolder, isDriveLinked, state.localHandle, state.driveRootId, ensureDriveAuth, markDirty, state]
  );

  const loadFile = useCallback(
    async (relPath) => {
      const pathKey = normalizePathKey(relPath);
      if (!pathKey) return null;
      if (!hasWorkspace) return null;

      if (hasFolder) {
        const { subdirs, file } = splitPath(pathKey);
        try {
          return await readLocalBlob(state.localHandle, subdirs, file);
        } catch {
          return null;
        }
      }

      if (isDriveLinked && state.driveRootId) {
        await ensureDriveAuth();
        if (!hasDriveToken()) return null;
        try {
          return await readBlobFromFolder(state.driveRootId, driveNameFromPath(pathKey));
        } catch {
          return null;
        }
      }

      return null;
    },
    [hasWorkspace, hasFolder, isDriveLinked, state.localHandle, state.driveRootId, ensureDriveAuth, state]
  );

  const getFileUrl = useCallback(
    async (relPath) => {
      const pathKey = normalizePathKey(relPath);
      if (!pathKey) return null;

      const cached = fileUrlCacheRef.current.get(pathKey);
      if (cached?.url) return cached.url;

      const blob = await loadFile(pathKey);
      if (!blob) return null;
      const url = URL.createObjectURL(blob);
      fileUrlCacheRef.current.set(pathKey, { url, ts: Date.now() });
      return url;
    },
    [loadFile]
  );

  const deleteFile = useCallback(
    async (relPath) => {
      const pathKey = normalizePathKey(relPath);
      if (!pathKey) return false;
      if (!hasWorkspace) return false;

      // Revoke any cached object URL
      try {
        const cached = fileUrlCacheRef.current.get(pathKey);
        if (cached?.url) URL.revokeObjectURL(cached.url);
        fileUrlCacheRef.current.delete(pathKey);
      } catch {}

      if (hasFolder) {
        const { subdirs, file } = splitPath(pathKey);
        const ok = await deleteLocalFile(state.localHandle, subdirs, file);
        if (ok) markDirty();
        return ok;
      }

      if (isDriveLinked && state.driveRootId) {
        await ensureDriveAuth();
        if (!hasDriveToken()) return false;
        try {
          const ok = await deleteFileInFolder(state.driveRootId, driveNameFromPath(pathKey));
          if (ok) markDirty();
          return ok;
        } catch {
          return false;
        }
      }

      return false;
    },
    [hasWorkspace, hasFolder, isDriveLinked, state.localHandle, state.driveRootId, ensureDriveAuth, markDirty, state]
  );

  // After saveJSON exists, best-effort mirror DRIVE_CONFIG_PATH on connectDrive (non-blocking)
  useEffect(() => {
    (async () => {
      if (!hasWorkspace) return;
      if (!driveConfig) return;
      // Only try to mirror if it’s the active drive workspace (avoid writing for local)
      if (activeWorkspace?.type !== "drive") return;
      try {
        await saveJSON(DRIVE_CONFIG_PATH, driveConfig);
      } catch {}
    })();
  }, [driveConfig, hasWorkspace, activeWorkspace?.type, saveJSON]);

  const readJSON = loadJSON;
  const writeJSON = saveJSON;

  const readDomain = useCallback(
    async (domain, fallback = []) => {
      if (!hasWorkspace) return fallback;
      const file = DOMAIN_FILES[domain];
      if (!file) throw new Error(`Unknown domain: ${domain}`);
      const data = await loadJSON(file, fallback);
      return data ?? fallback;
    },
    [hasWorkspace, loadJSON]
  );

  const writeDomain = useCallback(
    async (domain, value) => {
      if (!hasWorkspace) return false;
      const file = DOMAIN_FILES[domain];
      if (!file) throw new Error(`Unknown domain: ${domain}`);
      await saveJSON(file, value);
      return true;
    },
    [hasWorkspace, saveJSON]
  );

  const masterSaveFiles = useCallback(async () => {
    if (!hasWorkspace) return { ok: false, reason: "no-workspace" };

    let wrote = 0;
    for (const [, file] of Object.entries(DOMAIN_FILES)) {
      const localMirror = localStorage.getItem(`mudo:${file}`);
      let toWrite = null;
      if (localMirror) {
        try {
          toWrite = JSON.parse(localMirror);
        } catch {}
      } else {
        toWrite = await loadJSON(file, null);
      }
      if (toWrite == null) continue;
      try {
        await saveJSON(file, toWrite);
        wrote++;
      } catch {}
    }
    return { ok: true, wrote };
  }, [hasWorkspace, loadJSON, saveJSON]);

  const masterReceiveFiles = useCallback(
    async ({ replaceMode = true } = {}) => {
      if (import.meta.env.DEV) {
        console.count("[Storage] masterReceiveFiles called");
      }
      if (receiveInFlightRef.current) return receiveInFlightRef.current;

      if (import.meta.env.DEV) {
        console.count("[Storage] masterReceiveFiles RUN (won lock)");
      }

      receiveInFlightRef.current = (async () => {
        if (!hasWorkspace) return { ok: false, reason: "no-workspace" };

        suspendDriveWritesRef.current = true;
        try {
          let pulled = 0;

          const canRead = hasFolder || (isDriveLinked && state.driveRootId && isDriveAuthed);
          if (!canRead) return { ok: false, reason: "no-storage" };

          for (const [, file] of Object.entries(DOMAIN_FILES)) {
            const remoteVal = await loadJSONFresh(file, null);
            if (remoteVal == null) continue;

            if (replaceMode) {
              await saveJSON(file, remoteVal);
            } else {
              const localVal = await loadJSON(file, null);
              let merged = remoteVal;
              if (Array.isArray(localVal) && Array.isArray(remoteVal)) {
                const byId = new Map();
                for (const it of [...localVal, ...remoteVal]) {
                  // ❌ NEVER generate new UUID during merge - skip items without ID
                  if (!it?.id) {
                    console.warn('[StorageContext] Skipping item without ID during merge:', it);
                    continue;
                  }
                  byId.set(it.id, it);
                }
                merged = Array.from(byId.values());
              } else if (localVal && typeof localVal === "object" && typeof remoteVal === "object") {
                merged = { ...localVal, ...remoteVal };
              }
              await saveJSON(file, merged);
            }
            pulled++;
          }

          try {
            window.dispatchEvent(new Event("mudo:files-restored"));
          } catch {}
          return { ok: true, pulled };
        } finally {
          suspendDriveWritesRef.current = false;
        }
      })().finally(() => {
        receiveInFlightRef.current = null;
      });

      return receiveInFlightRef.current;
    },
    [
      hasWorkspace,
      hasFolder,
      isDriveLinked,
      isDriveAuthed,
      state.driveRootId,
      loadJSONFresh,
      saveJSON,
      loadJSON,
    ]
  );

  const readRemoteVault = useCallback(async () => {
    if (!hasWorkspace) return null;
    const t0 = isDev ? performance.now() : 0;
    debugStorageLog("readRemoteVault start", { hasFolder, isDriveLinked, driveRootId: state.driveRootId });

    if (hasFolder) {
      try {
        const res = await readLocalJson(state.localHandle, [], "vault.json");
        if (isDev) debugStorageLog("readRemoteVault local done", { ms: Math.round(performance.now() - t0) });
        return res;
      } catch {
        return null;
      }
    }

    if (isDriveLinked && state.driveRootId) {
      await ensureDriveAuth();
      if (!hasDriveToken()) return null;
      try {
        const res = await readJsonFromFolder(state.driveRootId, "vault.json");
        try {
          const driveKey = `${state.driveRootId}:vault.json`;
          lastDrivePayloadRef.current.set(driveKey, stableStringify(res));
        } catch {}
        if (isDev) debugStorageLog("readRemoteVault drive done", { ms: Math.round(performance.now() - t0) });
        return res;
      } catch {
        return null;
      }
    }

    return null;
  }, [hasWorkspace, hasFolder, isDriveLinked, state.localHandle, state.driveRootId, ensureDriveAuth]);

  const writeRemoteVault = useCallback(
    async (vault) => {
      if (!hasWorkspace) return { where: "none", reason: "no-workspace" };

      if (hasFolder) {
        await writeLocalJson(state.localHandle, [], "vault.json", vault);
        return { where: "folder" };
      }

      if (isDriveLinked && state.driveRootId) {
        if (!hasHydratedRef.current) {
          driveDebug("skip drive write (vault) - not hydrated");
          return { where: "drive", reason: "not-hydrated" };
        }
        const driveKey = `${state.driveRootId}:vault.json`;
        const payloadStr = stableStringify(vault);
        if (lastDrivePayloadRef.current.get(driveKey) === payloadStr) {
          driveDebug("skip drive write (vault) - identical payload", driveKey);
          return { where: "drive", reason: "identical" };
        }
        await ensureDriveAuth();
        if (!hasDriveToken()) return { where: "error", reason: "no-token" };
        lastDrivePayloadRef.current.set(driveKey, payloadStr);
        await upsertJsonInFolder(state.driveRootId, "vault.json", vault);
        return { where: "drive" };
      }

      return { where: "none" };
    },
    [hasWorkspace, hasFolder, isDriveLinked, state.driveRootId, state.localHandle, ensureDriveAuth]
  );

  const saveAll = useCallback(
    async (opts = { force: false }) => {
      if (!hasWorkspace) return { where: "none", reason: "no-workspace" };

      setLastConflict(null);
      const snapshot = snapshotLocalStorage();
      const nextVault = buildVault(snapshot);
      const target = hasFolder ? MODE_LOCAL : isDriveLinked ? MODE_DRIVE : null;
      if (!target) return { where: "none" };

      const remote = await readRemoteVault();
      const remoteMeta = remote?.meta || null;
      const lastSynced = readMetaFromLS(target);

      const conflict =
        remoteMeta && lastSynced && parseTime(remoteMeta.updatedAt) > parseTime(lastSynced.updatedAt);

      if (conflict && !opts.force) {
        const where = hasFolder ? "folder" : "drive";
        const res = { where, conflict: true, remoteMeta, lastSynced };
        setLastConflict(res);
        return res;
      }

      const wrote = await writeRemoteVault(nextVault);
      if (wrote.where === "folder" || wrote.where === "drive") {
        writeMetaToLS(target, nextVault.meta);
        return { ...wrote, bytes: JSON.stringify(nextVault).length, meta: nextVault.meta };
      }
      return wrote;
    },
    [hasWorkspace, hasFolder, isDriveLinked, readRemoteVault, writeRemoteVault]
  );

  const forceSend = useCallback(async () => saveAll({ force: true }), [saveAll]);

  // MAIN RECEIVE HANDLER (vault reload)
  const reload = useCallback(async () => {
    // ✅ FIX #2: Return existing promise if reload already in progress (single-flight)
    if (reloadInFlightRef.current) {
      console.log("[Storage] reload already in progress, returning existing promise");
      return reloadInFlightRef.current;
    }

    if (!hasWorkspace) {
      console.warn("[Storage] reload blocked: no workspace", {
        hasFolder,
        isDriveLinked,
        isDriveAuthed,
        activeWorkspace,
      });
      return { ok: false, reason: "no-workspace" };
    }
    suspendDriveWritesRef.current = true;

    // ✅ FIX #2: Create async IIFE and track it
    reloadInFlightRef.current = (async () => {

    const tReload = isDev ? performance.now() : 0;
    try {
      setReceiving(true);
      setLastConflict(null);

      console.log("[Storage] reload start", {
        hasFolder,
        isDriveLinked,
        isDriveAuthed,
        driveRootId: state.driveRootId,
      });

      const remote = await readRemoteVault();
      if (!remote || !remote.data) {
        console.warn("[Storage] reload no vault found");
        try {
          window.dispatchEvent(
            new CustomEvent("mudo:reload-progress", {
              detail: { phase: "error", processed: 0, total: 0 },
            })
          );
        } catch {}
        return { ok: false, reason: "no-vault" };
      }

      const keys = remote?.data ? Object.keys(remote.data) : [];
      const totalKeys = keys.length;
      let processedKeys = 0;

      if (typeof window !== "undefined" && totalKeys > 0) {
        window.dispatchEvent(
          new CustomEvent("mudo:reload-progress", {
            detail: { phase: "start", processed: 0, total: totalKeys },
          })
        );
      }

      let count = 0;
      for (const k of keys) {
        const v = remote.data[k];
        if (shouldSkipKey(k)) {
          console.log("[Storage] reload: skip reserved key", k);
          processedKeys += 1;
          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new CustomEvent("mudo:reload-progress", {
                detail: { phase: "applying", processed: processedKeys, total: totalKeys },
              })
            );
          }
          continue;
        }

        // If this is a mirrored domain key (mudo:<path>), restore via saveJSON
        if (k.startsWith("mudo:")) {
          const path = k.slice(5);
          try {
            // ✅ FIX #1: Use coerceJSON instead of JSON.parse to handle raw strings
            const parsed = typeof v === "string" ? coerceJSON(v) : v;
            if (path === "studio/studioProfile.json") {
              console.log("[Storage] reload: applying studioProfile from Drive", parsed);
            }
            await saveJSON(path, parsed);
            count++;
          } catch (e) {
            console.warn("[Storage] reload failed to saveJSON", path, e);
          }
          processedKeys += 1;
          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new CustomEvent("mudo:reload-progress", {
                detail: { phase: "applying", processed: processedKeys, total: totalKeys },
              })
            );
          }
          continue;
        }

        try {
          const safeVal = typeof v === "string" ? v : JSON.stringify(v);
          localStorage.setItem(k, safeVal);
          count++;
        } catch (e) {
          console.warn("[Storage] reload failed to setItem", k, e);
        }

        processedKeys += 1;
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("mudo:reload-progress", {
              detail: { phase: "applying", processed: processedKeys, total: totalKeys },
            })
          );
        }
      }

      if (typeof window !== "undefined" && totalKeys > 0) {
        window.dispatchEvent(
          new CustomEvent("mudo:reload-progress", {
            detail: { phase: "done", processed: totalKeys, total: totalKeys },
          })
        );
      }

      try {
        window.dispatchEvent(new Event("mudo:vault-restored"));
        window.dispatchEvent(new Event("mudo:files-restored"));
      } catch {}

      const target = hasFolder ? MODE_LOCAL : isDriveLinked ? MODE_DRIVE : null;
      if (target && remote.meta) writeMetaToLS(target, remote.meta);

      console.log("[Storage] reload complete", { count });
      if (isDev) debugStorageLog("reload timing", { ms: Math.round(performance.now() - tReload) });
      return { ok: true, keys: count, meta: remote.meta || null };
    } catch (err) {
      console.error("[Storage] reload error", err);
      try {
        window.dispatchEvent(
          new CustomEvent("mudo:reload-progress", {
            detail: { phase: "error", processed: 0, total: 0 },
          })
        );
      } catch {}
      return { ok: false, reason: "exception", error: err?.message || String(err) };
    } finally {
      suspendDriveWritesRef.current = false;
      setReceiving(false);
      // ✅ FIX #2: Clear in-flight ref so next reload can proceed
      reloadInFlightRef.current = null;
    }
    })(); // End of async IIFE

    // ✅ FIX #2: Return the tracked promise
    return reloadInFlightRef.current;
  }, [
    hasWorkspace,
    hasFolder,
    isDriveLinked,
    isDriveAuthed,
    state.driveRootId,
    readRemoteVault,
    saveJSON,
    activeWorkspace,
  ]);

  const exportAll = useCallback(async () => {
    const bundle = snapshotLocalStorage();
    const name = `${new Date().toISOString().replace(/[:.]/g, "-")}.mudo.json`;

    if (hasFolder) {
      await writeLocalJson(state.localHandle, ["backups"], name, bundle);
      return { where: "folder", name };
    }
    if (isDriveLinked && state.driveRootId) {
      if (!hasHydratedRef.current) {
        driveDebug("skip drive export - not hydrated");
        return { where: "drive", name, reason: "not-hydrated" };
      }
      await ensureDriveAuth();
      if (!hasDriveToken()) return { where: "error" };
      const backupsId = await ensureMudoBackups(state.driveRootId);
      const driveKey = `${backupsId}:${name}`;
      const payloadStr = stableStringify(bundle);
      if (lastDrivePayloadRef.current.get(driveKey) === payloadStr) {
        driveDebug("skip drive export - identical payload", driveKey);
        return { where: "drive", name, reason: "identical" };
      }
      lastDrivePayloadRef.current.set(driveKey, payloadStr);
      await upsertJsonInFolder(backupsId, name, bundle);
      return { where: "drive", name };
    }

    const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
    URL.revokeObjectURL(a.href);
    return { where: "download", name };
  }, [hasFolder, isDriveLinked, state.localHandle, state.driveRootId, ensureDriveAuth]);

  const importBackup = useCallback(async (file) => {
    const text = await file.text();
    const obj = JSON.parse(text);
    for (const [k, v] of Object.entries(obj.data || {})) {
      localStorage.setItem(k, typeof v === "string" ? v : JSON.stringify(v));
    }
    try {
      window.dispatchEvent(new Event("mudo:restore-complete"));
    } catch {}
    return { ok: true, keys: Object.keys(obj.data || {}).length };
  }, []);

  // Unified Receive with progress phases
  const receiveFromDrive = useCallback(
    async (source = "manual") => {
      try {
        setReceiveSource(source || "manual");
        setRestorePhase("starting");
        setRestoreProgress(5);
        await new Promise((r) => setTimeout(r, 150));

        setRestorePhase("listing");
        setRestoreProgress(25);
        await new Promise((r) => setTimeout(r, 150));

        const onProgress = (e) => {
          const phase = e?.detail?.phase;
          const processed = e?.detail?.processed ?? 0;
          const total = e?.detail?.total ?? 0;
          if (phase === "start") {
            setRestorePhase("downloading");
            setRestoreProgress(30);
          } else if (phase === "applying") {
            setRestorePhase("downloading");
            const pct = total > 0 ? 30 + Math.round((processed / total) * 55) : restoreProgress + 1;
            setRestoreProgress(Math.min(90, pct));
          } else if (phase === "done") {
            setRestorePhase("hydrating");
            setRestoreProgress(90);
          } else if (phase === "error") {
            setRestorePhase("error");
            setRestoreProgress(0);
          }
        };

        window.addEventListener("mudo:reload-progress", onProgress);
        const res = await reload();
        window.removeEventListener("mudo:reload-progress", onProgress);

        if (res?.ok) {
          setRestorePhase("done");
          setRestoreProgress(100);
          setTimeout(() => {
            setRestorePhase("idle");
            setRestoreProgress(0);
            setReceiveSource("none");
          }, 1500);
        } else {
          setRestorePhase("error");
          setRestoreProgress(0);
          setReceiveSource("none");
        }
        return res;
      } catch (err) {
        console.error("[StorageContext] Receive error", err);
        setRestorePhase("error");
        setRestoreProgress(0);
        setReceiveSource("none");
        throw err;
      }
    },
    [reload, restoreProgress]
  );

  const healthCheck = useCallback(async () => {
    if (!hasWorkspace) return { ok: false, reason: "no-workspace" };

    if (hasFolder) {
      const ok = await verifyLocalWrite(state.localHandle);
      return { ok, mode: MODE_LOCAL };
    }
    if (isDriveLinked && state.driveRootId) {
      await ensureDriveAuth();
      const ok = await driveHealth(state.driveRootId);
      return { ok, mode: MODE_DRIVE };
    }
    return { ok: false, reason: "no-storage" };
  }, [hasWorkspace, hasFolder, isDriveLinked, state.localHandle, state.driveRootId, ensureDriveAuth]);

  const lastSynced = useMemo(() => (mode ? readMetaFromLS(mode) : null), [mode, lastConflict, hasFolder, isDriveLinked]);

  // Turn off bootstrapping indicator once workspace is active
  useEffect(() => {
    if (bootstrappingDrive && hasWorkspace) setBootstrappingDrive(false);
  }, [bootstrappingDrive, hasWorkspace]);

  // ---------------------------------------------------------------------------
  // Auto-hydrate ONCE per workspace (prevents repeated Drive fetch loops)
  // ---------------------------------------------------------------------------
  const didAutoHydrateRef = useRef(new Set());
  const didAutoReceiveHydrateRef = useRef(new Set());
  useEffect(() => {
    if (!hasWorkspace || !activeWorkspace) return;

    const key = `${activeWorkspace.type}:${activeWorkspace.id}`;
    if (!key) return;
    if (didAutoHydrateRef.current.has(key)) return;
    hasHydratedRef.current = false;
    didAutoHydrateRef.current.add(key);

    (async () => {
      const targets = [
        "materials",
        "packaging",
        "staff",
        "utilities",
        "marketing",
        "tasks",
        "planner",
        "products",
        "inspiration",
        "receipts",
      ];

      let emptyCount = 0;

      for (const d of targets) {
        try {
          const file =
            DOMAIN_FILES[`${d}_domain`] ||
            DOMAIN_FILES[`${d}_file`] ||
            DOMAIN_FILES[`${d}_items`] ||
            DOMAIN_FILES[d] ||
            null;

          const cur = file ? await loadJSON(file, null) : await readDomain(d, null);
          if (cur == null || (Array.isArray(cur) && cur.length === 0)) emptyCount++;
        } catch {
          emptyCount++;
        }
      }

      // If all domains empty AND workspace exists, hydrate once
      if (emptyCount === targets.length) {
        try {
          if (!didAutoReceiveHydrateRef.current.has(key)) {
            didAutoReceiveHydrateRef.current.add(key);
            await masterReceiveFiles({ replaceMode: true });
          }
        } catch {}
      }
      hasHydratedRef.current = true;
      driveDebug("hydration complete", key);
    })();
    // Intentionally only keyed to workspace identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasWorkspace, activeWorkspace?.type, activeWorkspace?.id]);

  const ensureDir = useCallback(async (_dir) => true, []);

  // Forget per-user remembered local folder on this device (does not disconnect current workspace)
  const forgetRememberedFolder = useCallback(async () => {
    try {
      const uid = user?.id;
      if (!uid) return false;
      const key = FOLDER_HANDLE_KEY(uid);
      await deleteFolderHandle(key);
      try {
        localStorage.removeItem(FOLDER_META_KEY(uid));
      } catch {}
      return true;
    } catch (e) {
      console.warn("[StorageContext] forgetRememberedFolder failed", e);
      return false;
    }
  }, [user?.id]);

  const value = useMemo(
    () => ({
      mode,
      hasFolder,
      hasWorkspace,
      activeWorkspace,
      baseDirName,
      workspaceName,
      isDriveLinked,
      driveConfig,
      bootstrappingDrive,
      isDriveAuthed,
      busy,
      receiving,
      lastSynced,
      lastConflict,
      restorePhase,
      restoreProgress,

      pickFolder,
      clearFolderSelection,
      linkLocalFolder: pickFolder,
      connectDrive,
      disconnectDrive,

      loadJSON,
      loadJSONFresh,
      saveJSON,

      // Binary files
      saveFile,
      loadFile,
      getFileUrl,
      deleteFile,

      readJSON,
      writeJSON,

      readDomain,
      writeDomain,

      masterSaveFiles,
      masterReceiveFiles,

      saveAll,
      reload,
      receiveFromDrive,
      forceSend,

      exportAll,
      importBackup,
      healthCheck,

      ensureDir,
      requestDriveTokenInteractive,
      markDirty,
      markSynced,
      forgetRememberedFolder,
    }),
    [
      mode,
      hasFolder,
      hasWorkspace,
      activeWorkspace,
      baseDirName,
      workspaceName,
      isDriveLinked,
      driveConfig,
      bootstrappingDrive,
      isDriveAuthed,
      busy,
      receiving,
      lastSynced,
      lastConflict,
      restorePhase,
      restoreProgress,

      pickFolder,
      clearFolderSelection,
      connectDrive,
      disconnectDrive,

      loadJSON,
      loadJSONFresh,
      saveJSON,

      saveFile,
      loadFile,
      getFileUrl,
      deleteFile,

      readJSON,
      writeJSON,

      readDomain,
      writeDomain,

      masterSaveFiles,
      masterReceiveFiles,

      saveAll,
      reload,
      receiveFromDrive,
      forceSend,

      exportAll,
      importBackup,
      healthCheck,

      ensureDir,
      requestDriveTokenInteractive,
      markDirty,
      markSynced,
      forgetRememberedFolder,
    ]
  );

  return <StorageContext.Provider value={value}>{children}</StorageContext.Provider>;
}
